/**
 * Options Dashboard Component Identifiers
 * Standardized unique keys for each modular trading component on the Options Dashboard.
 */
export const OPTIONS_COMPONENTS = {
  ORDER_BOOK: "ORDER_BOOK",         // Contract Selector + Live Order Book (Asks & Bids) [OptionLeftTable]
  TRADE_HISTORY: "TRADE_HISTORY",   // Market Executed Trades ticker (Time, Amount, Price) [OptionRightTable]
  CHART: "CHART",                   // Options MultiChart / Depth Chart Module [OptionChart]
  BUY_SELL: "BUY_SELL",             // Options Order Placement Forms (Market, Limit, Stop) [OptionBuyAndSell]
  BOTTOM_TABLE: "BOTTOM_TABLE",     // User Positions, Open Orders, Trade History & Stop-Limit [OptionBottomTable]
  MARGIN_HOLDINGS: "MARGIN_HOLDINGS", // Options Margin & Crypto Holdings Module [OptionMarginHoldings]
};

/**
 * Metadata for each component for admin display, documentation and layout builders.
 */
export const OPTIONS_COMPONENT_META = {
  [OPTIONS_COMPONENTS.ORDER_BOOK]: {
    id: OPTIONS_COMPONENTS.ORDER_BOOK,
    name: "Order Book & Contract Selector",
    shortName: "Order Book",
    description: "Options contract search, strike filter dropdown, and live bid/ask order book",
    defaultWidth: { xs: 12, sm: 12, md: 2 },
  },
  [OPTIONS_COMPONENTS.TRADE_HISTORY]: {
    id: OPTIONS_COMPONENTS.TRADE_HISTORY,
    name: "Market Trade History",
    shortName: "Trade History",
    description: "Real-time stream of executed options trades with price, volume, and time",
    defaultWidth: { xs: 12, sm: 12, md: 2 },
  },
  [OPTIONS_COMPONENTS.CHART]: {
    id: OPTIONS_COMPONENTS.CHART,
    name: "Trading Chart (TV MultiChart & Depth)",
    shortName: "Chart",
    description: "Interactive TradingView multi-chart grid with Depth chart toggle",
    defaultWidth: { xs: 12, sm: 12, md: 8 },
  },
  [OPTIONS_COMPONENTS.BUY_SELL]: {
    id: OPTIONS_COMPONENTS.BUY_SELL,
    name: "Options Order Module",
    shortName: "Buy / Sell",
    description: "Order entry forms: Buy/Sell Call/Put, Limit, Market, and Stop-Limit",
    defaultWidth: { xs: 12, sm: 12, md: 8 },
  },
  [OPTIONS_COMPONENTS.BOTTOM_TABLE]: {
    id: OPTIONS_COMPONENTS.BOTTOM_TABLE,
    name: "User Positions & Orders",
    shortName: "Positions & Orders",
    description: "User positions, open orders, filled trades, and stop-limit records",
    defaultWidth: { xs: 12, md: 12 },
  },
  [OPTIONS_COMPONENTS.MARGIN_HOLDINGS]: {
    id: OPTIONS_COMPONENTS.MARGIN_HOLDINGS,
    name: "Margin & Crypto Holdings",
    shortName: "Crypto Holdings",
    description: "Cross margin balance, crypto spot wallet balances, transfer to margin, and options funding",
    defaultWidth: { xs: 12, sm: 12, md: 4 },
  },
};

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 1: DEFAULT (Paybito Classic 3-Column Standard) ────────────────────
// ══════════════════════════════════════════════════════════════════════════════
export const DEFAULT_OPTIONS_LAYOUT_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      {
        colId: "col-orderbook",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 position-relative p-0 orderbooktable",
        components: [{ key: OPTIONS_COMPONENTS.ORDER_BOOK }],
      },
      {
        colId: "col-chart-buysell",
        size: { xs: 12, sm: 12, md: 8 },
        className: "mb-1 px-1 spotchart",
        components: [
          { key: OPTIONS_COMPONENTS.CHART },
          { key: OPTIONS_COMPONENTS.BUY_SELL, sx: { mt: "6px" } },
        ],
      },
      {
        colId: "col-tradehistory",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 p-0 orderbooktable",
        components: [{ key: OPTIONS_COMPONENTS.TRADE_HISTORY }],
      },
    ],
  },
  {
    rowId: "bottom-history-row",
    columns: [
      {
        colId: "col-bottom-table",
        size: { xs: 12, md: 12 },
        className: "mb-4 p-0",
        components: [{ key: OPTIONS_COMPONENTS.BOTTOM_TABLE, sx: { mt: "6px" } }],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 2: BINANCE_PRO (Order Book Left, Chart Center, Buy/Sell Right) ───
// ══════════════════════════════════════════════════════════════════════════════
export const BINANCE_PRO_OPTIONS_LAYOUT_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      // Left Column: Order Book + Trade History stacked (compact width)
      {
        colId: "col-left-orderbook-trades",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 p-0",
        components: [
          { key: OPTIONS_COMPONENTS.ORDER_BOOK, flex: 1, height: "calc(50% - 3px)", minHeight: 0 },
          { key: OPTIONS_COMPONENTS.TRADE_HISTORY, flex: 1, height: "calc(50% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
      // Center Column: Chart + Bottom Tables stacked
      {
        colId: "col-center-chart-orders",
        size: { xs: 12, sm: 12, md: 6 },
        className: "mb-1 px-1",
        components: [
          { key: OPTIONS_COMPONENTS.CHART, flex: 1, height: "calc(50% - 3px)", minHeight: 0 },
          { key: OPTIONS_COMPONENTS.BOTTOM_TABLE, flex: 1, height: "calc(50% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
      // Right Column: Buy & Sell Order Entry (Top) + Margin Holdings (Bottom)
      {
        colId: "col-right-buysell",
        size: { xs: 12, sm: 12, md: 4 },
        className: "mb-1 p-0",
        components: [
          { key: OPTIONS_COMPONENTS.BUY_SELL, flex: 1, height: "calc(50% - 3px)", minHeight: 0 },
          { key: OPTIONS_COMPONENTS.MARGIN_HOLDINGS, flex: 1, height: "calc(50% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 3: CHART_RIGHT_STACKED_BOTTOM (User Requested Layout) ─────────────
// ══════════════════════════════════════════════════════════════════════════════
export const CHART_RIGHT_STACKED_BOTTOM_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      {
        colId: "col-orderbook",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 position-relative p-0 orderbooktable",
        components: [{ key: OPTIONS_COMPONENTS.ORDER_BOOK }],
      },
      {
        colId: "col-tradehistory-beside-left",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 p-0 orderbooktable",
        components: [{ key: OPTIONS_COMPONENTS.TRADE_HISTORY }],
      },
      {
        colId: "col-chart-buysell-right",
        size: { xs: 12, sm: 12, md: 8 },
        className: "mb-1 px-1 spotchart",
        components: [
          { key: OPTIONS_COMPONENTS.CHART },
          { key: OPTIONS_COMPONENTS.BUY_SELL, sx: { mt: "6px" } },
        ],
      },
    ],
  },
  {
    rowId: "mytrades-row",
    columns: [
      {
        colId: "col-bottom-table-full-width",
        size: { xs: 12, md: 12 },
        className: "mb-4 p-0",
        components: [{ key: OPTIONS_COMPONENTS.BOTTOM_TABLE, sx: { mt: "6px" } }],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 4: COINBASE_ADVANCED (Order Form Left, Book Center, Chart Right) ──
// ══════════════════════════════════════════════════════════════════════════════
export const COINBASE_ADVANCED_OPTIONS_LAYOUT_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      // Left Column: Buy & Sell Order Entry (Top) + Margin Holdings (Bottom)
      {
        colId: "col-left-buysell",
        size: { xs: 12, sm: 12, md: 4 },
        className: "mb-1 p-0",
        components: [
          { key: OPTIONS_COMPONENTS.BUY_SELL, flex: 1, height: "calc(50% - 3px)", minHeight: 0 },
          { key: OPTIONS_COMPONENTS.MARGIN_HOLDINGS, flex: 1, height: "calc(50% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
      // Center-Left Column: Order Book & Trades (compact width)
      {
        colId: "col-center-orderbook",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 px-1",
        components: [
          { key: OPTIONS_COMPONENTS.ORDER_BOOK, flex: 1, height: "calc(50% - 3px)", minHeight: 0 },
          { key: OPTIONS_COMPONENTS.TRADE_HISTORY, flex: 1, height: "calc(50% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
      // Right Column: Chart on top, Positions / Orders stacked below
      {
        colId: "col-right-chart-trades",
        size: { xs: 12, sm: 12, md: 6 },
        className: "mb-1 p-0",
        components: [
          { key: OPTIONS_COMPONENTS.CHART, flex: 1, height: "calc(50% - 3px)", minHeight: 0 },
          { key: OPTIONS_COMPONENTS.BOTTOM_TABLE, flex: 1, height: "calc(50% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 5: FOUR_COLUMN_PANORAMIC (Ultra-Wide Pro Execution Grid) ──────────
// ══════════════════════════════════════════════════════════════════════════════
export const FOUR_COLUMN_PANORAMIC_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      {
        colId: "col-orderbook",
        size: { xs: 12, sm: 6, md: 2 },
        className: "mb-1 position-relative p-0 four-col-orderbook",
        components: [{ key: OPTIONS_COMPONENTS.ORDER_BOOK }],
      },
      {
        colId: "col-tradehistory",
        size: { xs: 12, sm: 6, md: 2 },
        className: "mb-1 p-0 four-col-trades",
        components: [{ key: OPTIONS_COMPONENTS.TRADE_HISTORY }],
      },
      {
        colId: "col-chart",
        size: { xs: 12, sm: 12, md: 5 },
        className: "mb-1 px-1 four-col-chart",
        components: [
          { key: OPTIONS_COMPONENTS.CHART, flex: 1, height: "100%" },
        ],
      },
      {
        colId: "col-buysell-side",
        size: { xs: 12, sm: 12, md: 3 },
        className: "mb-1 p-0 four-col-buysell",
        components: [
          { key: OPTIONS_COMPONENTS.BUY_SELL, flex: 1, height: "calc(50% - 3px)", minHeight: 0 },
          { key: OPTIONS_COMPONENTS.MARGIN_HOLDINGS, flex: 1, height: "calc(50% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
    ],
  },
  {
    rowId: "bottom-history-row",
    columns: [
      {
        colId: "col-bottom-table",
        size: { xs: 12, md: 12 },
        className: "mb-4 p-0",
        components: [{ key: OPTIONS_COMPONENTS.BOTTOM_TABLE, sx: { mt: "6px" } }],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 6: CHART_LEFT_INVERTED (Chart on Left, Order Book on Right) ───────
// ══════════════════════════════════════════════════════════════════════════════
export const CHART_LEFT_INVERTED_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      {
        colId: "col-chart-buysell-left",
        size: { xs: 12, sm: 12, md: 8 },
        className: "mb-1 px-1 spotchart",
        components: [
          { key: OPTIONS_COMPONENTS.CHART },
          { key: OPTIONS_COMPONENTS.BUY_SELL, sx: { mt: "6px" } },
        ],
      },
      {
        colId: "col-orderbook-right",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 position-relative p-0 orderbooktable",
        components: [{ key: OPTIONS_COMPONENTS.ORDER_BOOK }],
      },
      {
        colId: "col-tradehistory-right",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 p-0 orderbooktable",
        components: [{ key: OPTIONS_COMPONENTS.TRADE_HISTORY }],
      },
    ],
  },
  {
    rowId: "bottom-history-row",
    columns: [
      {
        colId: "col-bottom-table",
        size: { xs: 12, md: 12 },
        className: "mb-4 p-0",
        components: [{ key: OPTIONS_COMPONENTS.BOTTOM_TABLE, sx: { mt: "6px" } }],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 7: SWAPPED_BOTTOM_CENTER (Chart + History Center, Buy/Sell Bottom)─
// ══════════════════════════════════════════════════════════════════════════════
export const SWAPPED_BOTTOM_CENTER_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      {
        colId: "col-orderbook",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 position-relative p-0 orderbooktable",
        components: [{ key: OPTIONS_COMPONENTS.ORDER_BOOK }],
      },
      {
        colId: "col-tradehistory-beside-left",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 p-0 orderbooktable",
        components: [{ key: OPTIONS_COMPONENTS.TRADE_HISTORY }],
      },
      {
        colId: "col-chart-right",
        size: { xs: 12, sm: 12, md: 8 },
        className: "mb-1 px-1 spotchart",
        components: [
          { key: OPTIONS_COMPONENTS.CHART, flex: 1, height: "calc(50% - 3px)", minHeight: 0 },
          { key: OPTIONS_COMPONENTS.BOTTOM_TABLE, flex: 1, height: "calc(50% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
    ],
  },
  {
    rowId: "bottom-swapped-row",
    columns: [
      {
        colId: "col-buysell-full-width",
        size: { xs: 12, md: 12 },
        className: "mb-4 p-0",
        components: [{ key: OPTIONS_COMPONENTS.BUY_SELL, sx: { mt: "6px" } }],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET REGISTRY & METADATA ────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
export const OPTIONS_LAYOUT_PRESETS = {
  DEFAULT: DEFAULT_OPTIONS_LAYOUT_MATRIX,
  BINANCE_PRO: BINANCE_PRO_OPTIONS_LAYOUT_MATRIX,
  CHART_RIGHT_STACKED_BOTTOM: CHART_RIGHT_STACKED_BOTTOM_MATRIX,
  COINBASE_ADVANCED: COINBASE_ADVANCED_OPTIONS_LAYOUT_MATRIX,
  FOUR_COLUMN_PANORAMIC: FOUR_COLUMN_PANORAMIC_MATRIX,
  CHART_LEFT_INVERTED: CHART_LEFT_INVERTED_MATRIX,
  SWAPPED_BOTTOM_CENTER: SWAPPED_BOTTOM_CENTER_MATRIX,
};

export const OPTIONS_LAYOUT_PRESET_LIST = [
  {
    id: "DEFAULT",
    settingId: 1,
    name: "Classic",
    description: "Left OrderBook | Center Chart & Buy/Sell | Right Trade History | Bottom Table",
    matrix: DEFAULT_OPTIONS_LAYOUT_MATRIX,
  },
  {
    id: "BINANCE_PRO",
    settingId: 2,
    name: "Advanced",
    description: "Left OrderBook & Trades | Center Chart & History | Right Buy/Sell Panel",
    matrix: BINANCE_PRO_OPTIONS_LAYOUT_MATRIX,
  },
  {
    id: "CHART_RIGHT_STACKED_BOTTOM",
    settingId: 3,
    name: "Chart Right",
    description: "Left OrderBook & Trades | Right Chart & Buy/Sell | Bottom My Trades",
    matrix: CHART_RIGHT_STACKED_BOTTOM_MATRIX,
  },
  {
    id: "COINBASE_ADVANCED",
    settingId: 4,
    name: "Pro Trader",
    description: "Left Buy/Sell | Center OrderBook & Trades | Right Chart & Positions",
    matrix: COINBASE_ADVANCED_OPTIONS_LAYOUT_MATRIX,
  },
  {
    id: "FOUR_COLUMN_PANORAMIC",
    settingId: 5,
    name: "4-Column",
    description: "Col 1 OrderBook | Col 2 Trades | Col 3 Big Chart | Col 4 Buy/Sell",
    matrix: FOUR_COLUMN_PANORAMIC_MATRIX,
  },
  {
    id: "CHART_LEFT_INVERTED",
    settingId: 6,
    name: "Chart Left",
    description: "Left Big Chart & Buy/Sell | Right OrderBook & Trade History | Bottom Table",
    matrix: CHART_LEFT_INVERTED_MATRIX,
  },
  {
    id: "SWAPPED_BOTTOM_CENTER",
    settingId: 7,
    name: "Swapped",
    description: "Left 2 Tables | Center Chart & Positions | Full-Width Buy/Sell at Bottom",
    matrix: SWAPPED_BOTTOM_CENTER_MATRIX,
  },
];

/**
 * Retrieve the current layout matrix directly reflecting the Spot/Futures layout
 */
export const getOptionsLayoutMatrix = () => {
  try {
    const spotPresetId = localStorage.getItem("admin_spot_layout_preset") || "DEFAULT";
    if (spotPresetId && OPTIONS_LAYOUT_PRESETS[spotPresetId]) {
      return OPTIONS_LAYOUT_PRESETS[spotPresetId];
    }
  } catch (err) {
    console.error("Error reading layout preset for options:", err);
  }
  return DEFAULT_OPTIONS_LAYOUT_MATRIX;
};

/**
 * Switch preset and broadcast to the dashboard in real-time
 */
export const setOptionsLayoutPreset = (presetId) => {
  if (OPTIONS_LAYOUT_PRESETS[presetId]) {
    try {
      localStorage.setItem("admin_spot_layout_preset", presetId);
      localStorage.removeItem("admin_options_layout_matrix");
    } catch (e) {
      console.error(e);
    }
    window.dispatchEvent(
      new CustomEvent("options_layout_change", { detail: OPTIONS_LAYOUT_PRESETS[presetId] })
    );
    window.dispatchEvent(
      new CustomEvent("spot_layout_change", { detail: presetId })
    );
    return true;
  }
  return false;
};
